import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowRight, Filter } from 'lucide-react';
import { portfolioImages } from '../data/mockData';

const Portfolio = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = [
    { value: 'all', label: 'All Projects' },
    { value: 'exterior', label: 'Exteriors' },
    { value: 'interior', label: 'Interiors' }
  ];

  const filteredImages = selectedCategory === 'all'
    ? portfolioImages
    : portfolioImages.filter(img => img.category === selectedCategory);

  return (
    <div className="min-h-screen bg-white pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-6xl font-bold text-[#262626] mb-6">
              Our Portfolio
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              Explore our completed custom homes and witness the quality craftsmanship that defines EC Home Builds. Each project represents our commitment to excellence and attention to detail.
            </p>
          </div>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-8 bg-white border-b border-gray-200 sticky top-20 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center space-x-2 text-gray-600">
              <Filter size={20} />
              <span className="font-medium">Filter by:</span>
            </div>
            <div className="flex flex-wrap gap-2 justify-center">
              {categories.map(category => (
                <Button
                  key={category.value}
                  onClick={() => setSelectedCategory(category.value)}
                  variant={selectedCategory === category.value ? 'default' : 'outline'}
                  className={selectedCategory === category.value
                    ? 'bg-[#DAA520] hover:bg-[#B8860B] text-white'
                    : 'border-gray-300 text-gray-700 hover:border-[#DAA520] hover:text-[#DAA520]'
                  }
                >
                  {category.label}
                </Button>
              ))}
            </div>
            <div className="text-sm text-gray-600">
              Showing <span className="font-semibold text-[#DAA520]">{filteredImages.length}</span> projects
            </div>
          </div>
        </div>
      </section>

      {/* Portfolio Grid */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredImages.map((image) => (
              <Card key={image.id} className="group overflow-hidden hover:shadow-2xl transition-all duration-300 border-2 border-gray-100 hover:border-[#DAA520]">
                <div className="relative overflow-hidden aspect-[4/3]">
                  <img
                    src={image.url}
                    alt={image.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <Badge className="bg-[#DAA520] text-white mb-2">
                        {image.category}
                      </Badge>
                      <h3 className="text-xl font-bold text-white">{image.title}</h3>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Stats */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 text-center">
            {[
              { value: '11+', label: 'Years Experience' },
              { value: '50+', label: 'Homes Built' },
              { value: '7', label: 'Floor Plans' },
              { value: '100%', label: 'Client Satisfaction' }
            ].map((stat, index) => (
              <div key={index} className="p-8">
                <div className="text-5xl font-bold text-[#DAA520] mb-2">{stat.value}</div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Project Types */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#262626] mb-4">
              What We Build
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From custom single-family homes to investment properties, we bring expertise to every project type
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: 'Custom Homes',
                description: 'Build your dream home on your lot with complete design flexibility and premium craftsmanship.',
                image: 'https://imgproxy.gamma.app/resize/quality:80/resizing_type:fit/width:2000/https://cdn.gamma.app/8ez3v3g1znwt914/90fe7512dd9d4297a0aa035f0019ea99/optimized/tempImageRMn3Vv.jpg'
              },
              {
                title: 'Spec Homes',
                description: 'Move-in ready homes built with the same quality standards and attention to detail.',
                image: 'https://imgproxy.gamma.app/resize/quality:80/resizing_type:fit/width:2000/https://cdn.gamma.app/8ez3v3g1znwt914/13fac9c9e904445684c93c7dfde3bc98/optimized/tempImagesgZkX4.jpg'
              },
              {
                title: 'Investment Properties',
                description: 'Strategic builds for investors including single-family homes and duplexes for strong ROI.',
                image: 'https://imgproxy.gamma.app/resize/quality:80/resizing_type:fit/width:2000/https://cdn.gamma.app/8ez3v3g1znwt914/37d56fcee08a40fcad7ceda69d2079ee/optimized/tempImageAYcKTe.jpg'
              }
            ].map((type, index) => (
              <Card key={index} className="group overflow-hidden hover:shadow-2xl transition-all duration-300">
                <div className="relative overflow-hidden h-64">
                  <img
                    src={type.image}
                    alt={type.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <CardContent className="p-6">
                  <h3 className="text-2xl font-bold text-[#262626] mb-3">{type.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{type.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#DAA520]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Start Your Project?
          </h2>
          <p className="text-xl mb-8 text-white/90">
            Let's discuss how we can bring your vision to life with the same quality and craftsmanship you see here.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="bg-[#262626] hover:bg-black text-white px-8 py-6 text-lg">
                Schedule Consultation
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
            <Link to="/floor-plans">
              <Button variant="outline" className="bg-white/10 backdrop-blur-md border-white text-white hover:bg-white hover:text-[#262626] px-8 py-6 text-lg">
                Browse Floor Plans
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Portfolio;
